/**
 * #357: Office, Company and Department offer the customer's saved values and the ones its users
 * already carry, and accept a new value. Diflex curates the list in Hub: a value it has removed
 * stays out of the list even while users still carry it.
 */

const isText = (value) => typeof value === "string" && value.trim() !== "";

const sameValue = (value) => value.trim().toLowerCase();

/**
 * The values offered for one field. Values compare without regard to case, so "stockholm" and
 * "Stockholm" are one value; a saved spelling wins over one a user carries, since that is where
 * Diflex corrects it.
 *
 * @param {string} field - "Office", "Company" or "Department"
 * @param {object[]} listValues - The tenant's saved values: { field, value, isHidden }
 * @param {object[]} users - The users whose values count (callers leave out rooms and the like)
 * @param {string} userField - The user property holding the value, e.g. "officeLocation"
 * @param {string[]} [configured] - Values from the SCO form settings, offered to every customer
 * @returns {string[]} Sorted distinct values, without the removed ones
 */
export const offeredListValues = (field, listValues, users, userField, configured = []) => {
  const saved = (Array.isArray(listValues) ? listValues : []).filter(
    (listValue) => listValue?.field === field && isText(listValue.value)
  );
  const removed = new Set(
    saved.filter((listValue) => listValue.isHidden).map((listValue) => sameValue(listValue.value))
  );
  const candidates = [
    ...saved.filter((listValue) => !listValue.isHidden).map((listValue) => listValue.value),
    ...(configured || []),
    ...(Array.isArray(users) ? users : []).map((user) => user?.[userField]),
  ];

  const offered = new Map();
  candidates.filter(isText).forEach((value) => {
    const key = sameValue(value);
    if (!removed.has(key) && !offered.has(key)) offered.set(key, value.trim());
  });

  return [...offered.values()].sort((a, b) => a.localeCompare(b, "sv"));
};

/**
 * The values Diflex has removed from one field, for the hub's list administration.
 *
 * @param {string} field - "Office", "Company" or "Department"
 * @param {object[]} listValues - The tenant's saved values: { field, value, isHidden }
 * @returns {string[]} Sorted removed values
 */
export const removedListValues = (field, listValues) =>
  (Array.isArray(listValues) ? listValues : [])
    .filter((listValue) => listValue?.field === field && listValue.isHidden && isText(listValue.value))
    .map((listValue) => listValue.value)
    .sort((a, b) => a.localeCompare(b, "sv"));
